import React, { useState, useEffect } from 'react';
import { PAYPAL_PLANS_URL } from '../../config/api';
import { authenticatedFetch } from '../Utils/apiHelpers';
import './SubscriptionPlans.css';

interface PlanProps {
  selectedPlan: string | null;
  onSelectPlan: (plan: string) => void;
  onContinue: () => void;
  onShowTerms: () => void;
  hasPurchasedPlan?: boolean;
}

interface ApiPlan {
  plan_type: string;
  name: string;
  charged_amount: number;
  charged_currency: string;
  display_amount: number;
  display_currency: string;
  duration_days: number;
}

interface DisplayPlan {
  id: string;
  title: string;
  price: string;
  description: string;
}

const FALLBACK_PLANS: DisplayPlan[] = [
  { id: 'weekly', title: 'Weekly', price: '£3.99/week', description: 'Great for regular users' },
  { id: 'monthly', title: 'Monthly', price: '£4.99/month', description: 'Our most popular plan' },
  { id: 'yearly', title: 'Yearly', price: '£49.99/year', description: 'Best value for committed foodies' }
];

const formatPrice = (amount: number, currency: string, interval: string): string => {
  const symbol = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$';
  return `${symbol}${amount}/${interval}`;
};

const getDescription = (id: string): string => {
  if (id === 'weekly') return 'Great for regular users';
  if (id === 'monthly') return 'Our most popular plan';
  if (id === 'yearly') return 'Best value for committed foodies';
  return '';
};

const mapApiPlansToDisplay = (plans: ApiPlan[]): DisplayPlan[] => {
  const intervalMap: Record<number, string> = { 7: 'week', 30: 'month', 365: 'year' };
  return plans.map(plan => {
    const id = plan.plan_type.toLowerCase();
    const interval = intervalMap[plan.duration_days] || id;
    return {
      id,
      title: plan.name,
      price: formatPrice(plan.display_amount, plan.display_currency, interval),
      description: getDescription(id)
    };
  });
};

const SubscriptionPlans: React.FC<PlanProps> = ({
  selectedPlan,
  onSelectPlan,
  onContinue,
  onShowTerms,
  hasPurchasedPlan = false
}) => {
  const [plans, setPlans] = useState<DisplayPlan[]>(FALLBACK_PLANS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await authenticatedFetch(PAYPAL_PLANS_URL, {
          method: 'GET',
          headers: { 'Accept': '*/*' },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.plans && Array.isArray(data.plans) && data.plans.length > 0) {
            setPlans(mapApiPlansToDisplay(data.plans));
          }
        }
      } catch {
        // Keep fallback plans on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handlePlanSelect = (plan: string, disabled: boolean) => {
    if (!disabled) {
      onSelectPlan(plan);
    }
  };



  return (
    <section id="subscription-section" className="plans-section">
      <div className="plans-container">
        <h2 className="plans-heading">Choose Your Subscription Plan</h2>
        <p className="plans-subheading">
          Subscribe to unlock all HomeCookt features and start your culinary journey
        </p>

        <div className="plans-grid">
          {isLoading ? (
            <div className="plans-loading">Loading plans...</div>
          ) : (
            plans.map(plan => (
              <div
                key={plan.id}
                className={`plan-card ${selectedPlan === plan.id ? 'selected' : ''}`}
                onClick={() => handlePlanSelect(plan.id, false)}
              >
                <h3 className="plan-title">{plan.title}</h3>
                <div className="plan-price">{plan.price}</div>
                <p className="plan-description">{plan.description}</p>
              </div>
            ))
          )}
        </div>

        <p className="benefits-text">
          {hasPurchasedPlan
            ? 'Select a different plan above to upgrade or downgrade your subscription.'
            : 'Subscribing now gives you early access benefits. After launch, prices will go up and visibility priority may not be guaranteed.'}
        </p>

        <button
          className="continue-button"
          onClick={onContinue}
          disabled={!selectedPlan || isLoading}
        >
          {hasPurchasedPlan ? 'Continue to Switch Plan' : 'Continue to Payment'}
        </button>

        <p className="terms-text">
          By continuing, you agree to our{' '}
          <button onClick={onShowTerms}>Terms & Conditions</button>
        </p>
      </div>
    </section>
  );
};

export default SubscriptionPlans;
